// Search form base class
class SearchForm extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    if (!this.input) return;

    // Listen for form reset
    this.boundOnFormReset = this.onFormReset.bind(this);
    this.input.form?.addEventListener("reset", this.boundOnFormReset);

    // Keep submitted full-search parameters aligned with predictive search.
    this.boundOnSubmit = this.onSubmit.bind(this);
    this.input.form?.addEventListener("submit", this.boundOnSubmit);

    // Listen for input changes (ultra-responsive 50ms debounce)
    this.boundOnChange = webvista.debounce((event) => {
      this.onChange(event);
    }, 50);
    this.input.addEventListener("input", this.boundOnChange);

    // Pop panel for header search
    if (this.hasAttribute("data-pop-panel")) {
      this.boundOnFocusin = this.onFocusin.bind(this);
      this.boundOnFocusout = this.onFocusout.bind(this);
      this.input.addEventListener("focusin", this.boundOnFocusin);
      this.addEventListener("focusout", this.boundOnFocusout);
    }

    // Instant outside tap / click listener to close search box when tapping outside
    this.boundOnDocumentPointerDown = this.onDocumentPointerDown.bind(this);
    document.addEventListener("pointerdown", this.boundOnDocumentPointerDown, true);

    // Escape key listener to close search box
    this.boundOnKeydown = this.onKeydown.bind(this);
    this.addEventListener("keydown", this.boundOnKeydown);

    // Listen for speech recognition results
    this.boundHandleSpeechResult = this.handleSpeechResult.bind(this);
    this.addEventListener("speech-result", this.boundHandleSpeechResult);
  }

  disconnectedCallback() {
    if (this.boundOnFormReset)
      this.input?.form?.removeEventListener("reset", this.boundOnFormReset);
    if (this.boundOnSubmit)
      this.input?.form?.removeEventListener("submit", this.boundOnSubmit);
    if (this.boundOnChange)
      this.input?.removeEventListener("input", this.boundOnChange);
    if (this.boundOnFocusin)
      this.input?.removeEventListener("focusin", this.boundOnFocusin);
    if (this.boundOnFocusout)
      this.removeEventListener("focusout", this.boundOnFocusout);
    if (this.boundOnDocumentPointerDown)
      document.removeEventListener("pointerdown", this.boundOnDocumentPointerDown, true);
    if (this.boundOnKeydown)
      this.removeEventListener("keydown", this.boundOnKeydown);
    if (this.boundHandleSpeechResult)
      this.removeEventListener("speech-result", this.boundHandleSpeechResult);
  }

  /**
   * Toggle reset button visibility based on input value.
   */
  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains("hidden");
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove("hidden");
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add("hidden");
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  /**
   * Use title-only fields for a one-character full search so broad product
   * types and tags cannot overwhelm actual product-name matches.
   */
  onSubmit() {
    const fieldsInput = this.input.form?.querySelector(
      'input[name="options[fields]"]',
    );
    if (!fieldsInput) return;

    fieldsInput.value =
      this.input.value.trim().length === 1
        ? "title,variants.title"
        : "title,variants.title,product_type,tag";
  }

  /**
   * Handle form reset.
   * @param {Event} event
   */
  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();

    this.input.value = "";
    this.input.focus();

    // Manually dispatch input event since JS value changes don't trigger it
    this.input.dispatchEvent(
      new Event("input", {
        bubbles: true,
        cancelable: true,
      }),
    );
    this.toggleResetButton();
  }

  /**
   * Handle input focus in.
   */
  onFocusin() {
    this.classList.add("in-focus");
    this.isOpen = true;
    this.input.setAttribute("aria-expanded", "true");

    // Activate header to show background color on transparent headers
    this.toggleHeaderActive(true);
  }

  /**
   * Handle document pointerdown / outside tap to close search immediately.
   */
  onDocumentPointerDown(event) {
    if (!this.isOpen && !this.classList.contains("in-focus") && !this.hasAttribute("open")) return;
    if (this.contains(event.target)) return;

    // Tapped/clicked outside search container - close immediately
    this.closeSearch();
  }

  /**
   * Handle Escape key press.
   */
  onKeydown(event) {
    if (event.key === "Escape") {
      this.closeSearch();
    }
  }

  /**
   * Close search panel and deactivate active states.
   */
  closeSearch() {
    this.classList.remove("in-focus");
    this.isOpen = false;
    this.input?.setAttribute("aria-expanded", "false");
    this.toggleHeaderActive(false);

    if (typeof this.closeResults === "function") {
      this.closeResults(false);
    }
  }

  /**
   * Handle search focus out.
   */
  onFocusout() {
    // Defer to get the latest focused element, as focus changes are asynchronous
    setTimeout(() => {
      if (this.contains(document.activeElement)) return;

      this.closeSearch();
    });
  }

  /**
   * Toggle header active state.
   * @param {boolean} active
   */
  toggleHeaderActive(active = true) {
    this.header = this.header || this.closest(".section-header");
    if (this.header) this.header.classList.toggle("header--is-active", active);
  }

  /**
   * Handle speech recognition result.
   * @param {CustomEvent} event
   */
  handleSpeechResult(event) {
    const { transcript } = event.detail;
    if (!transcript) return;

    this.input.value = transcript;
    this.input.focus();
    this.input.dispatchEvent(
      new Event("input", {
        bubbles: true,
        cancelable: true,
      }),
    );
  }
}

window.SearchForm = SearchForm;

if (!customElements.get("search-form")) {
  customElements.define("search-form", SearchForm);
}

/**
 * Predictive search class.
 */
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};

    // Product type selector
    this.typeSelector = this.querySelector(
      'select[name="filter.p.product_type"]',
    );
    this.searchTerm = "";
    this.renderedQueryKey = "";
    this.isOpen = false;
  }

  connectedCallback() {
    super.connectedCallback();

    this.searchResultsWrapper = this.querySelector(".search-results-wrapper");

    // Popular keyword click listeners
    this.boundOnPopularKeywordClick = this.onPopularKeywordClick.bind(this);
    this.querySelectorAll(".popular-searches a").forEach((suggestion) => {
      suggestion.addEventListener("click", this.boundOnPopularKeywordClick);
    });

    this.abortController = new AbortController();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.abortController.abort();

    // Remove popular keyword listeners
    if (this.boundOnPopularKeywordClick) {
      this.querySelectorAll(".popular-searches a").forEach((suggestion) => {
        suggestion.removeEventListener(
          "click",
          this.boundOnPopularKeywordClick,
        );
      });
    }
  }

  /**
   * Build the search query string, including product type filter if selected.
   * @returns {string}
   */
  getQuery() {
    let query = this.input.value.trim();
    const type = this.typeSelector?.value;
    if (type) {
      query += ` AND product_type:${type}`;
    }

    return query;
  }

  /**
   * Return a collision-safe cache key for the visible term and product filter.
   * @returns {string}
   */
  getCacheKey() {
    const term = this.input.value.trim().replace(/\s+/g, " ").toLowerCase();
    const type = this.typeSelector?.value?.trim().toLowerCase() || "";
    return JSON.stringify([term, type]);
  }

  /**
   * Handle input value change and trigger search.
   */
  onChange() {
    super.onChange();

    const currentSearchTerm = this.getQuery();
    if (currentSearchTerm === this.searchTerm) return;
    this.searchTerm = currentSearchTerm;

    // Cancel the previous query before consulting the cache. Otherwise an older
    // response can arrive after a cached result and replace it with stale markup.
    this.abortController?.abort();

    if (this.input.value.trim().length <= 0) return this.closeResults(true);

    const queryKey = this.getCacheKey();
    if (this.cachedResults[queryKey]) {
      return this.renderSearchResults(this.cachedResults[queryKey], queryKey);
    }

    this.classList.add("in-focus");
    this.removeAttribute("results");
    this.renderedQueryKey = "";
    this.searchResultsWrapper.innerHTML = "";
    this.showPlaceholder();
    this.showResults();
    this.getSearchResults();
  }

  /**
   * Handle popular keyword click.
   * @param {Event} event
   */
  onPopularKeywordClick(event) {
    event.preventDefault();

    const keyword = event.currentTarget.getAttribute("data-keyword");
    if (keyword !== "") {
      this.input.value = keyword;
      this.input.focus();

      // Manually dispatch input event since JS value changes don't trigger it
      this.input.dispatchEvent(
        new Event("input", {
          bubbles: true,
          cancelable: true,
        }),
      );
    }
  }

  onFormReset(event) {
    super.onFormReset(event);

    this.searchTerm = "";
    this.renderedQueryKey = "";
    this.abortController.abort();
    this.abortController = new AbortController();
    this.closeResults(true);
  }

  /**
   * Handle input focus in, re-check search term and display results.
   */
  onFocusin() {
    super.onFocusin();

    // Re-check query in case search term changed from another input
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm && currentSearchTerm === this.searchTerm) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.searchResultsWrapper?.innerHTML?.trim()?.length > 0) {
      this.setAttribute("results", "true");
      this.showResults();
    } else if (this.searchTerm.trim().length > 0) {
      this.getSearchResults();
    }
  }

  /**
   * Show loading placeholder while fetching results.
   */
  showPlaceholder() {
    if (!this.searchResultsWrapper.innerHTML.trim()) {
      this.placeholder =
        this.placeholder ||
        this.querySelector(`#Placeholder-Search-Form-${this.dataset.section}`)
          ?.innerHTML;
      if (this.placeholder) {
        this.searchResultsWrapper.innerHTML = this.placeholder;
      }
    }
  }

  /**
   * Fetch search results from predictive search API.
   * Uses cached results when available.
   */
  getSearchResults() {
    const typedTerm = this.input.value.trim();
    const queryKey = this.getCacheKey();
    this.setLiveRegionLoading();

    // Use cached data if available
    if (this.cachedResults[queryKey])
      return this.renderSearchResults(this.cachedResults[queryKey], queryKey);

    // Abort previous in-flight request immediately for sudden, crisp responsiveness
    this.abortController?.abort();
    this.abortController = new AbortController();

    // Shopify applies prefix matching to the last token. For a one-character
    // query, restrict searchable fields to titles so broad tags/types cannot
    // overwhelm genuine product-name matches.
    const searchableFields =
      typedTerm.length === 1
        ? "title,variants.title"
        : "title,variants.title,product_type,tag";
    const url = new URL(
      window.routes.predictive_search_url || "/search/suggest",
      window.location.origin,
    );
    const searchParams = new URLSearchParams({
      q: typedTerm,
      "resources[type]": "product",
      "resources[limit]": "10",
      "resources[limit_scope]": "each",
      "resources[options][fields]": searchableFields,
      "resources[options][prefix]": "last",
      "resources[options][unavailable_products]": "last",
      section_id: "predictive-search",
    });
    if (this.typeSelector?.value) {
      searchParams.set("filter.p.product_type", this.typeSelector.value);
    }
    url.search = searchParams;

    fetch(url.toString(), {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }

        return response.text();
      })
      .then((text) => {
        // A cancelled request can still resolve from an intermediary cache.
        // Never cache or render it unless it still belongs to the active input.
        if (queryKey !== this.getCacheKey()) return;

        const section = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search, .shopify-section");
        if (!section) throw new Error("Predictive search section was not returned");
        const resultsMarkup = section.innerHTML;

        // Sync cached results across all predictive search instances
        document
          .querySelectorAll("predictive-search")
          .forEach((instance) => {
            instance.cachedResults[queryKey] = resultsMarkup;
          });

        if (queryKey === this.getCacheKey()) {
          this.renderSearchResults(resultsMarkup, queryKey);
        }
      })
      .catch((error) => {
        if (error.name === "AbortError" || queryKey !== this.getCacheKey()) return;

        this.removeAttribute("loading");
        this.searchResultsWrapper.innerHTML = "";
        this.removeAttribute("results");
      });
  }

  /**
   * Render search results HTML into the results container.
   * @param {string} resultsHtml
   * @param {string} queryKey
   */
  renderSearchResults(resultsHtml, queryKey) {
    if (queryKey !== this.getCacheKey()) return;

    // Reopening the same cached query should not rebuild every product card.
    if (
      this.renderedQueryKey === queryKey &&
      this.searchResultsWrapper.innerHTML.trim()
    ) {
      this.setAttribute("results", "true");
      this.setLiveRegionResults();
      this.showResults();
      return;
    }

    this.searchResultsWrapper.innerHTML = resultsHtml;
    this.renderedQueryKey = queryKey;
    this.setAttribute("results", "true");

    this.setLiveRegionResults();
    this.showResults();

    webvista.initLazyImages();
    webvista.initToolTips();
  }

  /**
   * Calculate and return the maximum height for the results container.
   * @returns {number}
   */
  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      this.querySelector(".predictive-search-main").getBoundingClientRect()
        .top -
      32;

    return this.resultsMaxHeight;
  }

  /**
   * Show predictive search results panel.
   * The 'open' attribute controls visibility.
   */
  showResults() {
    this.style.setProperty(
      "--result-max-height",
      `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`,
    );
    this.setAttribute("open", "true");
  }

  /**
   * Close predictive search results.
   * The 'open' attribute controls the results container visibility,
   * the 'results' attribute tracks whether data exists.
   * @param {boolean} clearSearchTerm - Whether to clear search data
   */
  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.searchResultsWrapper.innerHTML = "";
      this.renderedQueryKey = "";
    }

    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.removeAttribute("results");
    this.classList.remove("in-focus");
    this.isOpen = false;
    this.input?.setAttribute("aria-expanded", "false");
    this.toggleHeaderActive(false);

    this.resultsMaxHeight = false;
    this.style.removeProperty("--result-max-height");
  }

  /**
   * Set live region to loading state for assistive devices.
   */
  setLiveRegionLoading() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");
    this.setAttribute("loading", "true");
    this.setLiveRegionText(this.loadingText);
  }

  /**
   * Update live region with search results count for assistive devices.
   */
  setLiveRegionResults() {
    this.removeAttribute("loading");
    const countElement = this.querySelector(
      "[data-predictive-search-live-region-count-value]",
    );
    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    }
  }

  /**
   * Set live region text for assistive devices.
   * @param {string} statusText
   */
  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }
}

if (!customElements.get("predictive-search")) {
  customElements.define("predictive-search", PredictiveSearch);
}
